import {useState} from 'react';
import {DatePicker, Select} from 'antd';
import moment from 'moment';
import {
  LANGUAGE_OPTIONS,
  PRESETS,
  PRODUCT_OPTIONS,
  UNATTRIBUTED,
  agentName,
  presetRange,
  rangeError,
} from './performanceUtils';
import styles from './performance.module.scss';

const CUSTOM = 'custom';

/**
 * Period, product, language and (on the overview) agents. Every change goes
 * through `update`, which writes the URL and applies the spec's resets.
 */
export const PerformanceFilterBar = ({
  filters,
  update,
  availableAgents,
  showAgents,
}) => {
  const [rangeProblem, setRangeProblem] = useState(null);
  const [pickingCustom, setPickingCustom] = useState(false);
  const periodValue = filters.preset || CUSTOM;
  const showPicker = periodValue === CUSTOM || pickingCustom;

  const choosePeriod = value => {
    setRangeProblem(null);
    if (value === CUSTOM) {
      setPickingCustom(true);
      return;
    }
    setPickingCustom(false);
    update({...presetRange(value), preset: value});
  };

  const chooseRange = range => {
    if (!range?.[0] || !range?.[1]) return;
    // The picker's days are calendar days; read them as UTC days as-is.
    const from = range[0].format('YYYY-MM-DD');
    const to = range[1].format('YYYY-MM-DD');
    const problem = rangeError(from, to);
    setRangeProblem(problem);
    if (!problem) update({from, to});
  };

  const agentOptions = [
    {value: UNATTRIBUTED, label: 'Unattributed (AI, FAQ, customer)'},
    ...(availableAgents || []).map(agent => ({
      value: agent.id,
      label: agentName(agent, agent.id),
    })),
  ];

  return (
    <div className={styles.filterBar}>
      <div className={styles.filterFields}>
        <Select
          className={styles.periodSelect}
          value={pickingCustom ? CUSTOM : periodValue}
          onChange={choosePeriod}
          options={[
            ...PRESETS.map(preset => ({
              value: preset.key,
              label: preset.label,
            })),
            {value: CUSTOM, label: 'Custom…'},
          ]}
        />
        {showPicker && (
          <DatePicker.RangePicker
            allowClear={false}
            value={[moment(filters.from), moment(filters.to)]}
            onChange={chooseRange}
            format="D MMM YYYY"
          />
        )}
        <Select
          className={styles.filterSelect}
          allowClear
          placeholder="All products"
          value={filters.product || undefined}
          onChange={product => update({product: product || null})}
          options={PRODUCT_OPTIONS}
        />
        <Select
          className={styles.filterSelect}
          allowClear
          placeholder="All ticket languages"
          value={filters.language || undefined}
          onChange={language => update({language: language || null})}
          options={LANGUAGE_OPTIONS}
        />
        {showAgents && (
          <Select
            className={styles.agentSelect}
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="All agents"
            optionFilterProp="label"
            value={filters.agents}
            onChange={agents => update({agents: agents.slice(0, 100)})}
            options={agentOptions}
          />
        )}
      </div>
      {rangeProblem && <p className={styles.rangeError}>{rangeProblem}</p>}
    </div>
  );
};
